import { useEffect, useRef } from 'react';
import { useRouterState } from '@tanstack/react-router';

function getSessionId(): string {
  const storageKey = 'cv_session_id';
  let sessionId = sessionStorage.getItem(storageKey);
  if (!sessionId) {
    sessionId = crypto.randomUUID();
    sessionStorage.setItem(storageKey, sessionId);
  }
  return sessionId;
}

function parseUserAgent(ua: string) {
  let browser = 'Unknown';
  let os = 'Unknown';
  let deviceType = 'Desktop';

  if (ua.includes('Firefox/')) browser = 'Firefox';
  else if (ua.includes('Edg/')) browser = 'Edge';
  else if (ua.includes('Chrome/')) browser = 'Chrome';
  else if (ua.includes('Safari/') && !ua.includes('Chrome')) browser = 'Safari';
  else if (ua.includes('Opera') || ua.includes('OPR/')) browser = 'Opera';

  if (ua.includes('Windows')) os = 'Windows';
  else if (ua.includes('Mac OS')) os = 'macOS';
  else if (ua.includes('Linux')) os = 'Linux';
  else if (ua.includes('Android')) os = 'Android';
  else if (ua.includes('iOS') || ua.includes('iPhone') || ua.includes('iPad')) os = 'iOS';

  if (ua.includes('Mobile') || (ua.includes('Android') && !ua.includes('Tablet'))) deviceType = 'Mobile';
  else if (ua.includes('Tablet') || ua.includes('iPad')) deviceType = 'Tablet';

  return { browser, os, deviceType };
}

export function usePageTracking() {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const trackedPaths = useRef<Set<string>>(new Set());

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const path = pathname;
    if (path.startsWith('/admin') || trackedPaths.current.has(path)) return;
    trackedPaths.current.add(path);

    let cancelled = false;

    const send = async () => {
      if (cancelled) return;
      const { restInsert } = await import('@/lib/supabase-rest');
      if (cancelled) return;

      const sessionId = getSessionId();
      const userAgent = navigator.userAgent;
      const { browser, os, deviceType } = parseUserAgent(userAgent);
      let referrer = document.referrer;
      if (referrer && referrer.includes(window.location.hostname)) referrer = '';

      const req = restInsert('page_views', {
        session_id: sessionId,
        path,
        referrer: referrer || null,
        user_agent: userAgent,
        browser,
        device_type: deviceType,
        os,
        screen_width: window.screen.width,
        screen_height: window.screen.height,
        country: null,
        city: null,
      });
      if (req) req.catch((err) => console.debug('Page tracking failed:', err));
    };

    const w = window as Window & {
      requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number;
    };
    let timer: number | undefined;
    if (typeof w.requestIdleCallback === 'function') {
      w.requestIdleCallback(() => { void send(); }, { timeout: 4000 });
    } else {
      timer = window.setTimeout(() => { void send(); }, 1500);
    }

    return () => {
      cancelled = true;
      if (timer) window.clearTimeout(timer);
    };
  }, [pathname]);
}
